import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle, XCircle, Exam } from "@phosphor-icons/react";
import api from "../../lib/api";

function scoreColor(p) {
  if (p >= 70) return "text-[#059669] bg-[#059669]/10 border-[#059669]/20";
  if (p >= 50) return "text-accent bg-accent/10 border-accent/20";
  return "text-destructive bg-destructive/10 border-destructive/20";
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminUserDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get(`/admin/results/${userId}`).then((res) => {
      setData(res.data);
      if (res.data.results.length) setExpanded(res.data.results[0].id);
    });
  }, [userId]);

  if (!data) return <div className="max-w-4xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading...</div>;

  const { user, results } = data;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 lg:py-14">
      <Link
        to="/admin/users"
        data-testid="back-to-users"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
      >
        <ArrowLeft size={16} /> Back to staff accounts
      </Link>

      <div className="mt-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-primary text-white flex items-center justify-center font-heading font-black text-xl">
          {user.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight text-foreground">{user.name}</h1>
          <p className="text-muted-foreground">{user.email}</p>
        </div>
      </div>

      {results.length === 0 ? (
        <div className="mt-10 rounded-2xl bg-white border border-border p-12 text-center">
          <Exam size={40} weight="duotone" className="mx-auto text-muted-foreground" />
          <p className="text-foreground font-medium mt-4">This staff member hasn't taken the test yet.</p>
        </div>
      ) : (
        <div className="mt-8 space-y-4" data-testid="user-attempts">
          {results.map((r, i) => {
            const open = expanded === r.id;
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
                className="rounded-xl bg-white border border-border overflow-hidden"
                data-testid={`attempt-${i}`}
              >
                <button
                  onClick={() => setExpanded(open ? null : r.id)}
                  className="w-full flex items-center justify-between gap-4 p-5 hover:bg-muted/40 transition-colors duration-200"
                >
                  <div className="text-left">
                    <p className="font-heading font-bold text-foreground">Attempt · {fmt(r.submitted_at)}</p>
                    <p className="text-sm text-muted-foreground">{r.score} of {r.total} correct</p>
                  </div>
                  <span className={`font-heading font-bold text-lg rounded-full border px-4 py-1 ${scoreColor(r.percentage)}`}>
                    {r.percentage}%
                  </span>
                </button>
                {open && (
                  <div className="px-5 pb-5 space-y-3 border-t border-border pt-4">
                    {r.details.map((d) => (
                      <div key={d.question_id} className="flex items-start gap-3">
                        {d.correct ? (
                          <CheckCircle size={20} weight="fill" className="text-[#059669] shrink-0 mt-0.5" />
                        ) : (
                          <XCircle size={20} weight="fill" className="text-destructive shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-foreground">{d.question}</p>
                          <p className="text-xs mt-1">
                            <span className="text-muted-foreground">Answered: </span>
                            <span className={d.correct ? "text-[#059669]" : "text-destructive"}>
                              {d.selected >= 0 ? d.options[d.selected] : "No answer"}
                            </span>
                            {!d.correct && (
                              <>
                                <span className="text-muted-foreground"> · Correct: </span>
                                <span className="text-[#059669]">{d.options[d.correct_index]}</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
